// Public hook: fetch shipping rates from /api/content and adapt to the
// shape ShippingCalculator expects. The static `/app/frontend/src/data/
// rates.js` file remains as a safety-net fallback in case the API is
// unreachable, so the calculator never goes blank.
//
// Source priority:
//   1. API (admin-editable, live)            ← single source of truth
//   2. Static rates.js fallback              ← only when API fails
//
// The `source` field in the returned state lets the caller surface a
// debug pill if needed (we don't surface it to end users — but the
// debug source is recorded in DevTools for troubleshooting).
import { useEffect, useReducer } from "react";
import { adaptApiRatesToCalc } from "@/data/ratesAdapter";
import { RATES as STATIC_RATES } from "@/data/rates";

const INITIAL = { rates: STATIC_RATES, source: "static-initial", loading: true };

function reducer(_state, action) {
  switch (action.type) {
    case "ok":
      return { rates: action.rates, source: action.source, loading: false };
    case "err":
      return { rates: STATIC_RATES, source: "static-fallback-error", loading: false };
    default:
      return _state;
  }
}

export function useShippingRates() {
  const [state, dispatch] = useReducer(reducer, INITIAL);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/content`, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const apiRates = data?.store_info?.shipping_rates;
        const adapted = adaptApiRatesToCalc(apiRates);
        if (adapted && Object.keys(adapted).length) {
          dispatch({ type: "ok", rates: adapted, source: "api" });
        } else {
          dispatch({ type: "ok", rates: STATIC_RATES, source: "static-fallback-empty" });
        }
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "err" });
      });
    return () => { cancelled = true; };
  }, []);

  return state;
}
